import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { assertIsClientContext, context } from '@adornis/baseql/server/context.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { setUserOnContext } from '@adornis/users/server/baseqlContextHelper.js';
import { getUserWithPassword } from '@adornis/users/server/util.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import * as crypto from 'node:crypto';
import { sendEmailVerificationEmail, sendExistingAccountEmail } from '../../server/utils/email.js';
import { Student } from '../student.js';
import { Teacher } from '../teacher.js';

/**
 * Email verification for password sign-ups, by a six-digit code from the mail.
 *
 * An account counts as verified only once `emailVerifiedAt` is set on the raw user document. A MISSING field
 * means unverified, on purpose: anything that creates an account without going through signUpMutation must
 * not slip through by default. Accounts that predate this feature get the field from
 * backfillEmailVerification(), Google sign-ins from the OAuth success handler, and a completed password reset
 * sets it too (for accounts that are no pending registration, see below), because the reset link proves control
 * of the mailbox in the same way.
 *
 * Until the code is entered, the account is a PENDING REGISTRATION (`emailVerification` set, `emailVerifiedAt`
 * missing). It never had a session and owns nothing but its user document and profile, and it belongs to nobody yet:
 * whoever registered chose the password, but the address may be someone else's (a typo, or on purpose). So nobody
 * else ever takes it over. A Google sign-in (google-sign-helper.ts) and a new sign-up with the address (sign-up.ts)
 * discard it, a password reset is not offered for it (password-reset.ts), and it is deleted 48 h after the sign-up.
 *
 * The code is entered in the tab that signed up, together with the account's password (verifyEmailCodeMutation).
 * Whoever only has the mailbox cannot use it, where a link would have signed them in. That tab also holds the
 * guest's conversation, which page.tsx claims right after the sign-in.
 *
 * Enforcement is server-side: password logins of unverified accounts are refused in server/authz/generic-ops.ts,
 * and the library's own registerUser operation is closed to clients there, so the only way to sign up is
 * signUpMutation, which never opens a session.
 */

/** Stable markers the client matches on to tell these cases apart from wrong credentials and from each other. */
export const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';
export const EMAIL_CODE_INVALID = 'EMAIL_CODE_INVALID';
export const EMAIL_CODE_EXPIRED = 'EMAIL_CODE_EXPIRED';

const CODE_TTL_MS = 15 * 60 * 1000;
const MAX_CODE_ATTEMPTS = 5;
// Caps the guesses per registration at MAX_CODE_ATTEMPTS each. A new sign-up for the address carries the count over
// (sign-up.ts), so starting again gives no further guesses either.
const MAX_CODES_PER_REGISTRATION = 5;
const PENDING_REGISTRATION_TTL_MS = 48 * 60 * 60 * 1000;
const EXPIRY_SWEEP_INTERVAL_MS = 60 * 60 * 1000;
const RESEND_COOLDOWN_MS = 60 * 1000;
const BACKFILL_LEDGER_ID = 'email-verification-backfill';

/**
 * Only the hash is stored. Six digits are no secret against an offline search, but the hash alone verifies nothing:
 * entering the code also takes the account's password. The user id keeps equal codes of two accounts apart.
 */
const hashCode = (userID: unknown, code: string) =>
  crypto
    .createHash('sha256')
    .update(`${String(userID)}:${code}`)
    .digest('hex');
const userCollection = () => getRawCollection(AdornisUser._collectionName);

/** Selector for pending registrations, see the comment at the top. */
export const PENDING_REGISTRATION = { emailVerifiedAt: { $exists: false }, emailVerification: { $exists: true } };

/** The first name from the account's profile, for the greeting. One profile per account, teacher or student. */
async function profileFirstName(userID: unknown) {
  for (const Profile of [Teacher, Student]) {
    const profile = await (
      await getRawCollection(Profile._collectionName)
    ).findOne({ userID: String(userID) }, { projection: { firstName: 1 } });
    if (typeof profile?.firstName === 'string' && profile.firstName.trim()) return profile.firstName;
  }
  return undefined;
}

// `object` rather than a field type: callers pass raw documents and library user types, neither of which declares it.
export const isEmailVerified = (user: object | null | undefined) =>
  !!(user as { emailVerifiedAt?: unknown } | null | undefined)?.emailVerifiedAt;

export const isPendingRegistration = (user: object | null | undefined) =>
  !!user && !isEmailVerified(user) && !!(user as { emailVerification?: unknown }).emailVerification;

/** Whether this pending registration may get another code. `emailVerification` must be in the projection. */
export const canSendCode = (user: object) =>
  Number((user as { emailVerification?: { codesSent?: unknown } }).emailVerification?.codesSent ?? 0) <
  MAX_CODES_PER_REGISTRATION;

export const emailNotVerifiedError = () =>
  new ExpectedError(`${EMAIL_NOT_VERIFIED}: the email address of this account is not confirmed yet`, {
    translationKey: 'users.email_not_verified',
  });

const codeInvalidError = () =>
  new ExpectedError(`${EMAIL_CODE_INVALID}: the verification code is wrong`, {
    translationKey: 'users.email_code_invalid',
  });

const codeExpiredError = () =>
  new ExpectedError(`${EMAIL_CODE_EXPIRED}: the verification code expired or was entered wrong too often`, {
    translationKey: 'users.email_code_expired',
  });

/** Marks the account verified. A no-op for accounts that already are, so their original timestamp stays. */
export async function markEmailVerified(userID: unknown) {
  const users = await userCollection();
  await users.updateOne(
    { _id: userID as any, emailVerifiedAt: { $exists: false } },
    { $set: { emailVerifiedAt: new Date() }, $unset: { emailVerification: '' } },
  );
}

/**
 * Makes a new account a pending registration. `carried` is the `emailVerification` of the pending registration it
 * replaces (sign-up.ts): its sign-up time and code count stay, so starting again neither extends the 48 h nor gives
 * more guesses.
 */
export async function startPendingRegistration(
  userID: string,
  carried?: { registeredAt?: unknown; codesSent?: unknown },
) {
  const users = await userCollection();
  await users.updateOne(
    { _id: userID as any },
    {
      $set: {
        emailVerification: {
          registeredAt: carried?.registeredAt instanceof Date ? carried.registeredAt : new Date(),
          codesSent: Number(carried?.codesSent ?? 0),
        },
      },
    },
  );
}

/** Mails a fresh code. It replaces the previous one and gets its own MAX_CODE_ATTEMPTS. */
export async function sendEmailVerification(user: { _id: unknown; email: string; firstName?: string }) {
  const code = crypto.randomInt(0, 1_000_000).toString().padStart(6, '0');
  const now = new Date();

  const users = await userCollection();
  await users.updateOne(
    { _id: user._id as any },
    {
      $set: {
        'emailVerification.codeHash': hashCode(user._id, code),
        'emailVerification.expiresAt': new Date(now.getTime() + CODE_TTL_MS),
        'emailVerification.sentAt': now,
        'emailVerification.attempts': 0,
      },
      $inc: { 'emailVerification.codesSent': 1 },
    },
  );

  await sendEmailVerificationEmail(user.email, code, String(user._id), user.firstName);
}

/**
 * Deletes a pending registration: its user document, then its profile. That is all it can own, it never had a
 * session. Checked on the user document itself, so a verified account is never touched.
 */
export async function discardPendingRegistration(userID: unknown) {
  const users = await userCollection();
  const { deletedCount } = await users.deleteOne({ _id: userID as any, ...PENDING_REGISTRATION });
  if (!deletedCount) return;
  await (await getRawCollection(Teacher._collectionName)).deleteMany({ userID: String(userID) });
  await (await getRawCollection(Student._collectionName)).deleteMany({ userID: String(userID) });
}

/** Pending registrations older than 48 h: never confirmed, so their name and school are kept no longer. */
export async function discardExpiredPendingRegistrations() {
  const cutoff = new Date(Date.now() - PENDING_REGISTRATION_TTL_MS);
  const users = await userCollection();
  const expired = await users
    .find({ ...PENDING_REGISTRATION, 'emailVerification.registeredAt': { $lt: cutoff } }, { projection: { _id: 1 } })
    .toArray();
  for (const { _id } of expired) await discardPendingRegistration(_id);
  if (expired.length) {
    logger.info({ context: 'EMAIL_VERIFICATION', count: expired.length }, 'Abgelaufene Registrierungen gelöscht');
  }
}

let expiryTimer: NodeJS.Timeout | null = null;

/** One run right away, then hourly, like the guest retention. */
export function startPendingRegistrationExpiry(): void {
  if (expiryTimer) return;
  const run = () =>
    void discardExpiredPendingRegistrations().catch(err =>
      logger.error({ err, context: 'EMAIL_VERIFICATION' }, 'Löschen abgelaufener Registrierungen fehlgeschlagen'),
    );
  run();
  expiryTimer = setInterval(run, EXPIRY_SWEEP_INTERVAL_MS);
  expiryTimer.unref();
}

export function stopPendingRegistrationExpiry(): void {
  if (!expiryTimer) return;
  clearInterval(expiryTimer);
  expiryTimer = null;
}

/**
 * Only with the account's password, like entering the code: the address alone must not make Gradias send mail to
 * it over and over. Both places that offer this (after signing up, after a refused login) have the password at hand.
 */
export const resendVerificationEmailMutation = registerMutation({
  operationName: 'resendVerificationEmailMutation',
  type: () => Boolean,
  params: [
    { name: 'email', type: () => String },
    { name: 'password', type: () => String },
  ],
  resolve: (email: string, password: string) => async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) return true;

    const user = await getUserWithPassword(normalizedEmail, password, {
      projection: { email: 1, emailVerifiedAt: 1, emailVerification: 1 },
    }).catch(() => null);

    // The same answer for unknown, verified, wrong-password and throttled accounts, so nothing can be probed.
    if (!user || isEmailVerified(user)) return true;
    await resendEmailVerificationIfDue(user);
    return true;
  },
});

/**
 * Sends a fresh code to a pending registration unless one went out within the cooldown or all its codes are used.
 * Never throws: callers answer the same whatever happens here, so a failure must not reveal that the account exists.
 */
async function resendEmailVerificationIfDue(user: {
  _id: unknown;
  email?: unknown;
  emailVerification?: { sentAt?: unknown; codesSent?: unknown };
}) {
  if (typeof user.email !== 'string' || !isPendingRegistration(user) || !canSendCode(user)) return;
  const lastSentAt = user.emailVerification?.sentAt;
  if (lastSentAt instanceof Date && Date.now() - lastSentAt.getTime() < RESEND_COOLDOWN_MS) return;

  try {
    await sendEmailVerification({ _id: user._id, email: user.email, firstName: await profileFirstName(user._id) });
  } catch (err) {
    // Already logged with the mail context in sendMail.
    logger.error({ err, userID: String(user._id), context: 'EMAIL_VERIFICATION' }, 'Resend fehlgeschlagen');
  }
}

/**
 * Tells the owner of an existing account that someone tried to sign up with their address, at most once per cooldown
 * (the time is kept in `signUpNoticeSentAt` on the raw user document). Never throws, like the resend above.
 */
export async function sendExistingAccountNoticeIfDue(user: {
  _id: unknown;
  email?: unknown;
  signUpNoticeSentAt?: unknown;
}) {
  if (typeof user.email !== 'string') return;
  const lastSentAt = user.signUpNoticeSentAt;
  if (lastSentAt instanceof Date && Date.now() - lastSentAt.getTime() < RESEND_COOLDOWN_MS) return;

  try {
    const users = await userCollection();
    await users.updateOne({ _id: user._id as any }, { $set: { signUpNoticeSentAt: new Date() } });
    await sendExistingAccountEmail(user.email, String(user._id), await profileFirstName(user._id));
  } catch (err) {
    logger.error({ err, userID: String(user._id), context: 'EMAIL_VERIFICATION' }, 'Hinweis-Mail fehlgeschlagen');
  }
}

/**
 * Confirms the address with the code from the mail and signs the session in. Takes the password as well: the code
 * proves the mailbox, the password that this is the person who registered. Either alone is not enough.
 */
export const verifyEmailCodeMutation = registerMutation({
  operationName: 'verifyEmailCodeMutation',
  type: () => Boolean,
  params: [
    { name: 'email', type: () => String },
    { name: 'password', type: () => String },
    { name: 'code', type: () => String },
  ],
  resolve: (email: string, password: string, code: string) => async () => {
    assertIsClientContext(context);

    const user = await getUserWithPassword(String(email ?? '').trim(), String(password ?? ''), {
      projection: { _id: 1, _class: 1, emailVerifiedAt: 1, emailVerification: 1 },
    }).catch(() => null);
    // A wrong password reads like a wrong code and uses up no attempt: without it nobody can guess anyway.
    if (!user || !isPendingRegistration(user)) throw codeInvalidError();

    // The attempt is used up BEFORE the code is compared, in one atomic update, so parallel guesses cannot get past
    // the limit.
    const users = await userCollection();
    const { modifiedCount } = await users.updateOne(
      {
        _id: user._id as any,
        'emailVerification.codeHash': { $exists: true },
        'emailVerification.expiresAt': { $gt: new Date() },
        'emailVerification.attempts': { $lt: MAX_CODE_ATTEMPTS },
      },
      { $inc: { 'emailVerification.attempts': 1 } },
    );
    if (!modifiedCount) throw codeExpiredError();

    const pending = (await users.findOne({ _id: user._id as any }, { projection: { emailVerification: 1 } }))
      ?.emailVerification;
    const expected = Buffer.from(String(pending?.codeHash ?? ''));
    const given = Buffer.from(hashCode(user._id, String(code ?? '').replace(/\s/g, '')));
    if (expected.length !== given.length || !crypto.timingSafeEqual(expected, given)) {
      throw Number(pending?.attempts ?? MAX_CODE_ATTEMPTS) >= MAX_CODE_ATTEMPTS
        ? codeExpiredError()
        : codeInvalidError();
    }

    // Guarded on the hash: a code works once, even when it is sent twice at the same time.
    const verified = await users.updateOne(
      { _id: user._id as any, 'emailVerification.codeHash': pending?.codeHash },
      { $set: { emailVerifiedAt: new Date() }, $unset: { emailVerification: '' } },
    );
    if (!verified.modifiedCount) throw codeExpiredError();

    await setUserOnContext({ _id: user._id, _class: user._class } as any);
    return true;
  },
});

/**
 * Marks every account that predates email verification as verified.
 *
 * ONE-SHOT, NOT SELF-RETIRING like progress-backfill.ts: new sign-ups also lack `emailVerifiedAt` until they
 * confirm, so a query-based "only what is still missing" run on the next boot would verify them unseen. A ledger
 * entry in `migrations` makes it run exactly once. It must finish before the server listens, or existing users
 * would be refused at login in the meantime.
 */
export async function backfillEmailVerification() {
  const ledger = await getRawCollection('migrations');
  if (await ledger.findOne({ _id: BACKFILL_LEDGER_ID as any })) return 0;

  const users = await userCollection();
  const { modifiedCount } = await users.updateMany(
    { emailVerifiedAt: { $exists: false } },
    { $set: { emailVerifiedAt: new Date(), emailVerifiedByBackfill: true } },
  );
  await ledger.insertOne({ _id: BACKFILL_LEDGER_ID as any, ranAt: new Date(), modifiedCount });
  logger.info({ modifiedCount, context: 'EMAIL_VERIFICATION' }, 'Bestehende Konten als verifiziert markiert');
  return modifiedCount;
}
